import { useCallback, useState } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import { useHistory } from 'react-router-dom';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import CompanyLogoUploader from '../../components/Company/LogoFreemiumUploader';
import {
  aobfStep2Mobile,
  uploadCompanyLogoCall,
} from '../../services/company_services';

const styles = () => ({
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 12,
  },
});

function getCompanyNameValue(accountDetails, asLoggedUserDetails) {
  const user = accountDetails.contacts.find(
    (contact) => contact.id === asLoggedUserDetails.userId,
  );

  if (
    user &&
    accountDetails.companyName !== `${user.givenName} ${user.surName}`
  ) {
    return accountDetails.companyName;
  }
  return '';
}

const CompanyOnboardingStep2 = () => {
  const { accountDetails, asLoggedUserDetails, editAccountInformation } =
    useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const classes = styles();
  const [companyName, setCompanyName] = useState(
    getCompanyNameValue(accountDetails, asLoggedUserDetails),
  );
  const [uploaderOutput, setUploaderOutput] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const history = useHistory();
  const noopStatus = useCallback(() => {}, []);

  async function sendFormData() {
    try {
      await aobfStep2Mobile({
        CONTACT_ID: asLoggedUserDetails.userId,
        COMPANY_NAME:
          companyName !== accountDetails.companyName
            ? companyName.replace(/advisory board/i, '').trim()
            : '',
      });

      return {
        companyName: companyName,
        displayName: companyName,
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
    }
  }

  async function sendOriginalLogo() {
    try {
      const response = await uploadCompanyLogoCall({
        CONTACT_ID: asLoggedUserDetails.userId,
        COMPANY_LOGO_EDITED: uploaderOutput.image,
        COMPANY_LOGO_ORIGINAL: uploaderOutput.originalImage,
      });
      return {
        image: {
          id: response.data.uploadCompanyLogo.id + '.png',
          location: `${process.env.REACT_APP_IMAGES_URL}${response.data.uploadCompanyLogo.id}.png`,
        },
        companyS3Logo: {
          id: response.data.uploadCompanyLogo.id + '.png',
          location: `${process.env.REACT_APP_IMAGES_URL}${response.data.uploadCompanyLogo.id}.png`,
        },
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
    }
  }

  const disableButton = () => {
    if (isLoading || !companyName.trim()) {
      return true;
    }
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const optimisticPayload = {
      companyName: companyName,
      displayName: companyName,
    };
    try {
      setIsLoading(true);

      const [payload1, payload2] = await Promise.all([
        sendFormData(),
        sendOriginalLogo(),
      ]);
      const mergedPayload = { ...optimisticPayload, ...payload1, ...payload2 };
      editAccountInformation(mergedPayload);

      history.push('/company/onboarding/step3');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Setup
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Step 2/6
          </Typography>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={(2 / 6) * 100}
          sx={{
            height: 8,
            borderRadius: 999,
            backgroundColor: '#E8EDF4',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              backgroundColor: '#2F6CFB',
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            lineHeight: '28px',
            letterSpacing: '-0.5px',
            color: '#000000',
            mb: 1,
          }}
        >
          Name your advisory board.
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '-0.43px',
            color: '#646D7A',
            mb: 2,
          }}
        >
          We recommend naming it after your organization, team, or project.
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g. Acme Inc"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          sx={{
            width: '100%',
            // maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              backgroundColor: '#F9FAFB',
              fontSize: '16px',
              '& fieldset': {
                borderColor: '#E5E7EB',
              },
              '&:hover fieldset': {
                borderColor: '#CBD5E1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3171F6',
                boxShadow: '0 0 0 2px rgba(49, 113, 246, 0.15)',
              },
            },
          }}
          inputProps={{ style: { color: '#374151' } }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '20px',
            lineHeight: '26px',
            letterSpacing: '-0.5px',
            color: '#0F172A',
            mb: 1,
          }}
        >
          Upload a logo for your board.
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '-0.25px',
            color: '#6B7280',
            mb: 3,
          }}
        >
          Advisors are more likely to join an advisory board with a logo or
          image.
        </Typography>
        <Box sx={{ maxWidth: 240, width: '100%' }}>
          <CompanyLogoUploader
            resultFiles={setUploaderOutput}
            status={noopStatus}
            emptyLogoImage
            imageOnly
            preloadOnboarding
          />
        </Box>
      </Box>

      <Grid container justifyContent="center">
        <Grid
          {...classes.gridItemProps}
          xs={12}
          display="flex"
          justifyContent="center"
        >
          <Button
            // type="submit"
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={disableButton()}
            sx={{
              borderRadius: '15px',
              backgroundColor: '#2F6CFB',
              '&:hover': {
                backgroundColor: '#2A60E0',
              },
              height: '52px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 700,
              width: '100%',
              mb: isSM ? '24px' : null,
              my: '20px',
            }}
          >
            {isLoading ? <LoadingLogoSkeleton /> : 'Continue'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
    </Box>
  );
};

export default CompanyOnboardingStep2;
